import dataModel from '../../models/DataModel';
import FileUtil from '../../helpers/FileUtil/FileUtil';
import ImageTools from '../../helpers/ImageTools';
import Metadata from '../../helpers/metadata/Metadata';
import MiscConstants from '../../constants/MiscConstants';
import MobileFiles from '../../helpers/MobileFiles';
import _ from 'lodash';

/**
 * PhotoUtil: 
 * Utility for manipulating photos and photo uploads
 */

async function selectMultipleFiles(files, onlyImagesAccepted, completionStep, task) {
  if (files.length > MiscConstants.MAX_NUMBER_OF_PHOTOS - completionStep.attachments.length) {
    showErrorModal(completionStep.attachments, onlyImagesAccepted);
  }
  else {
    let updatedCS = completionStep;
    for (let fileToUpload of files) {
      const resizedFile = await handleResizeFile(fileToUpload, FileUtil.getExtension(fileToUpload.name), null, 'library');
      const { file, fileExtension, resizedImage, exifData } = resizedFile;
      resizedImage.filename = await processMobileImageResults(fileToUpload, fileExtension);
      try {
        //reassign the updatedCS to get all new attachments
        updatedCS = await handleSaveFile(file, fileExtension, resizedImage, exifData, updatedCS, task);
      }
      catch (error) {
        throw error;
      }
    }
    return updatedCS;
  }
}

function showErrorModal(attachments, onlyImagesAccepted) {
  const numberOfFilesLeft = MiscConstants.MAX_NUMBER_OF_PHOTOS - attachments.length;
  let fileTypeStr = onlyImagesAccepted ? 'image' : 'file';
  const message = `Only ${MiscConstants.MAX_NUMBER_OF_PHOTOS} ${fileTypeStr}${MiscConstants.MAX_NUMBER_OF_PHOTOS == 1 ? ' is' : 's are'} allowed. 
          Please select no more than ${numberOfFilesLeft} ${fileTypeStr + (numberOfFilesLeft == 1 ? '' : 's')}.`;

  dataModel.showGlobalModal('SYSTEM_POPUP', {
    message: message,
    okButtonText: 'OK',
    oneButton: true,
    width: 320,
    height: 184,
    captionText: 'ERROR',
    handleOwnerOk: () => {},
  });
}

function handleResizeFile(file, fileExtension, exif, uploadMethod) {
  return new Promise((resolve) => {
    ImageTools.resize(file, dataModel.mobileBridge.isMobile ? null : { height: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT, width: MiscConstants.MAX_PHOTO_WIDTH_OR_HEIGHT }, exif, (resizedImage, result, exifData) => {
      resolve(parseFile(file, fileExtension, resizedImage, exifData, uploadMethod));
    });
  });
}

function parseFile(file, fileExtension, resizedImage, exifData, uploadMethod) {
  let reader = new FileReader();
  if (resizedImage.size > 26214400) {
    dataModel.displayWarnErrorMessage(
      'File upload size limit is 25 MB. Please upload a smaller file',
      4000,
      2
    );
  } else {
    if (file) {
      reader.onloadend = () => {
        const fileInfo = {
          file: file,
          imagePreviewUrl: reader.result,
          fileExtension: fileExtension,
          exifData: exifData,
        };
        return fileInfo;
      };
      if (uploadMethod == 'library' || uploadMethod == 'web') {
        const previewFile = {
          file: file,
          fileExtension: fileExtension,
          resizedImage: resizedImage,
          type: 'image',
          exifData: exifData,
        };
        return previewFile;
      } 

      else if (uploadMethod == 'camera') {
        const previewFile = {
          file: file,
          fileExtension: fileExtension,
          resizedImage: resizedImage,
          exifData: exifData,
        };

        return previewFile;
      }
    }
  }
}

function handleSaveFile(fileExtension, fileUpload, exifData, completionStep, task) {

  const partialAttachmentName = FileUtil.replaceSpecialCharacters(`${task.name}_${completionStep.name}_`);
  const attachmentName =  partialAttachmentName + determineSaveNumber(partialAttachmentName, completionStep) + (fileExtension ? ('.' + fileExtension) : '');
  const eventMetadatum = Metadata.generateMetadatum({ exifData: exifData });

  const clonedStep = _.cloneDeep(completionStep);
  let newAttachment = {};
  newAttachment.id = dataModel.attachmentsDAO.creationIndex;
  newAttachment.attachable_type = 'CompletionCriterion';
  newAttachment.attachable_id = completionStep.id;
  newAttachment.file_upload = fileUpload;
  newAttachment.name = attachmentName;
  clonedStep.attachments.push(newAttachment);
  clonedStep.completed_at = new Date().toJSON();
  clonedStep.status = 'complete';

  return dataModel.attachmentsDAO.createAttachment(newAttachment, eventMetadatum, { recordDescriptor: { attachment: { user: { } } } }).then(attachment => {
    return clonedStep;
  });
}

function handleSaveFileOnWeb(task, completionStep, file, resizedImage, exifData) {
  const clonedStep = _.clone(completionStep);
  if (clonedStep.attachments.length >= MiscConstants.MAX_NUMBER_OF_PHOTOS) {
    return;
  }
  let attachmentName = file.name;
  if (completionStep.requested_file_type && completionStep.requested_file_type == 'jpg') {
    const partialAttachmentName = FileUtil.replaceSpecialCharacters(`${task.name}_${completionStep.name}_`);
    attachmentName =  partialAttachmentName + determineSaveNumber(partialAttachmentName, completionStep) + '.' + FileUtil.getExtension(file.name);
  }
  const eventMetadatum = Metadata.generateMetadatum({ exifData: exifData });
  let attachment = {};
  attachment.id = dataModel.attachmentsDAO.creationIndex;
  attachment.attachable_type = 'CompletionCriterion';
  attachment.attachable_id = clonedStep.id;
  attachment.upload = resizedImage;
  attachment.name = attachmentName;
  attachment.photo_longitude = null;
  attachment.photo_latitude = null;
  attachment.photo_taken_at = null;
  // below concat is to ensure re-render takes place (i.e. ensure attachments changes)
  clonedStep.attachments.push(attachment);
  if(clonedStep.completion_type != 'inspection'){
    clonedStep.completed_at = new Date().toJSON();
    clonedStep.status = 'complete';
  }

  return dataModel.attachmentsDAO.createAttachment(attachment, false, eventMetadatum).then((updatedAttachment) => {
    return clonedStep;
  })
  .catch(error => {
    // TODO
  });
}

function determineSaveNumber(currentStepName, completionStep) {
  let attachmentNames = completionStep.attachments.map(attachment => FileUtil.trimExtension(attachment.name));

  for (let i = 1; i <= MiscConstants.MAX_NUMBER_OF_PHOTOS; i++) {
    let completeStepName = currentStepName + i;
    if (!attachmentNames.includes(completeStepName)) {
      return i;
    }
  }
}

function fixExtension(extension) {
  if(extension) {
    if (extension.match(/^.+\.[a-zA-Z]+\?[a-zA-Z0-9]+$/)) {
      let markStart = extension.lastIndexOf('?');
      if (markStart >= 0) {
        extension = extension.substring(0, markStart);
      }
    }
  }
  return extension;
}

async function processMobileImageResults(results, extension) {
  let filename = null;
  try {
    if (results instanceof Blob) {
      if (MobileFiles.available()) {
        try {
          filename = await MobileFiles.persistBlob(results, extension);
        } catch (error) {
          // for now leave filename as it is
        }
      }
    } else {
      // assume from camera plugin
      results = JSON.parse(results);
      if (MobileFiles.available() && results.filename) {
        // copy file from temporary storage to a persistent area (in case
        // offline and a rehydrate from redux occurs before the upload can
        // take place... this will prevent upload from being removed by the
        // mobile os)
        try {
          filename = await MobileFiles.persistFile(results.filename);
        } catch (error) {
          // for now leave filename as it is
        }
      }
    }
  } catch(e) {
    // ignore 
  }
  return filename;
}

export default {
  handleResizeFile,
  handleSaveFile,
  handleSaveFileOnWeb,
  parseFile,
  fixExtension,
  processMobileImageResults,
  selectMultipleFiles,
  showErrorModal,
};



// WEBPACK FOOTER //
// ./src/helpers/PhotoUtil/PhotoUtil.js